import {
  CircularSpinnerß,
  Label,
  RadioGroup,
  RadioGroupItem,
} from '@otter-ai/den-components';
import { useRouter } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { useCallback, useEffect, useState } from 'react';
import { PATH_URL } from '@/constants/path-constants';
import { OnOffSwitch } from '@/shared/components/OnOffSwitch';
import { BUILT_IN_APPS } from '@/shared/types/apps';
import {
  ValidZoomServiceType,
  ValidZoomServiceTypeValues,
} from '@/shared/types/auth';
import { useSyncedAccounts } from '../../hooks/useSyncedAccounts';
import { useMoreZoomIntegrationsStatus } from '../../hooks/zoom/useMoreZoomIntegrationsStatus';
import {
  useZoomSettings,
  type ZoomAccountServiceSettings,
} from '../../hooks/zoom/useZoomSettings';
import { trackZoom } from '../../tracking/zoom-tracking';
import type { ZoomSyncedAccount } from '../../type/apps';
import { showZoomReconnect } from '../../utils/zoom';
import { zoomServiceType } from '../../utils/zoomServiceType';
import { ZoomWarningErrorDisplay } from './ZoomWarningErrorDisplay';

/**
 * Manages additional Zoom integration settings for connected accounts.
 * Handles enabling/disabling different Zoom service types (Live Notes and Cloud Sync)
 * for each connected account.
 */
export function MoreZoomIntegrations() {
  const router = useRouter();
  const { showMoreZoomIntegrations } = useMoreZoomIntegrationsStatus();
  const {
    serviceSettings,
    disableAllZoomSettings,
    updateZoomSettings,
    isPending: isUpdateZoomSettingsPending,
  } = useZoomSettings();
  const [accountOptions, setAccountOptions] = useState<
    {
      account: ZoomSyncedAccount;
      enabled: boolean;
      enabledOption: ValidZoomServiceType | null | undefined;
    }[]
  >([]);

  useEffect(() => {
    setAccountOptions(
      serviceSettings.map((accountServiceSettings) => {
        const enabledOption = pickEnabledOption(accountServiceSettings);
        return {
          account: accountServiceSettings.account,
          enabled: enabledOption !== null,
          enabledOption,
        };
      }),
    );
  }, [serviceSettings]);

  const handleEnabledChanged = useCallback(
    (enabled: boolean, account: ZoomSyncedAccount) => {
      setAccountOptions((prev) => {
        return prev.map((accountOption) => {
          if (!accountOption?.account?.account) {
            console.error(
              'accountOption.account.account is undefined',
              accountOption,
            );
          } else if (accountOption.account.account === account.account) {
            return { account, enabled, enabledOption: null };
          }
          return accountOption;
        });
      });
      if (!enabled) {
        disableAllZoomSettings({
          accountsToDisable: [account],
        });
      }
    },
    [disableAllZoomSettings],
  );

  const handleEnabledOptionSelected = useCallback(
    async (option: ValidZoomServiceType, account: ZoomSyncedAccount) => {
      // TODO(SER-14251): Handled the toggling off the other options in the backend.
      // Toggle all other service types to false, except the one that was selected
      setAccountOptions((prev) => {
        return prev.map((accountOption) => {
          if (!accountOption?.account?.account) {
            console.error(
              'accountOption.account.account is undefined',
              accountOption,
            );
          } else if (accountOption.account.account === account.account) {
            return { account, enabled: true, enabledOption: option };
          }
          return accountOption;
        });
      });

      await Promise.all(
        ValidZoomServiceTypeValues.map((serviceType) =>
          updateZoomSettings({
            accountsToUpdate: [account],
            zoomServiceType: serviceType,
            enabled: option === serviceType,
          }),
        ),
      );

      if (option === ValidZoomServiceType.CLOUD_SYNC) {
        zoomServiceType.set(ValidZoomServiceType.CLOUD_SYNC, true);
        trackZoom.main.zoomConfigurationToggle(true, 'cloud_sync');
        router.push(PATH_URL.ZOOM_ONBOARDING);
      }
      if (option === ValidZoomServiceType.LIVE_NOTES) {
        zoomServiceType.set(ValidZoomServiceType.LIVE_NOTES, true);
        trackZoom.main.zoomConfigurationToggle(true, 'live_notes');
        router.push(PATH_URL.ZOOM_ONBOARDING);
      }
    },
    [updateZoomSettings, router, zoomServiceType],
  );
  if (!showMoreZoomIntegrations) {
    return null;
  }

  return (
    <>
      {accountOptions.map((accountOption) => (
        <div key={accountOption.account.account}>
          {accountOption.enabled && (
            <ZoomWarningErrorDisplay account={accountOption.account} />
          )}
          {isUpdateZoomSettingsPending && (
            <div className="flex justify-center items-center py-2">
              {/* TODO(sleahy): Improve the UX for when the settings are being updated */}
              <CircularSpinnerß
                size="sm"
                data-testid="zoom-settings-update-spinner"
              />
            </div>
          )}
          <ZoomSettings
            enabled={accountOption.enabled}
            enabledOption={accountOption.enabledOption}
            accountName={
              accountOptions.length > 1 ? accountOption.account.account : null
            }
            onEnabledChanged={(enabled) =>
              handleEnabledChanged(enabled, accountOption.account)
            }
            onEnabledOptionSelected={(option) =>
              handleEnabledOptionSelected(option, accountOption.account)
            }
          />
        </div>
      ))}
    </>
  );
}
type ZoomSettingsProps = {
  /** Whether the Zoom integration is enabled for this account */
  enabled: boolean;
  /** The currently selected service type (Live Notes or Cloud Sync) */
  enabledOption: ValidZoomServiceType | null | undefined;
  /** Callback when the enabled state changes */
  onEnabledChanged: (enabled: boolean) => void;
  /** Callback when a different service type is selected */
  onEnabledOptionSelected: (option: ValidZoomServiceType) => void;
  /** The Zoom account email address, shown when multiple accounts are connected */
  accountName?: string | null;
};

/**
 * Component for managing settings for a single Zoom account.
 * Allows toggling the integration on/off and selecting between Live Notes
 * and Cloud Sync service types.
 */
function ZoomSettings({
  enabled,
  enabledOption,
  onEnabledChanged,
  onEnabledOptionSelected,
  accountName,
}: ZoomSettingsProps) {
  const t = useTranslations('settings:integrations.apps.zoom.settings');

  return (
    <RadioGroup value={enabledOption} onValueChange={onEnabledOptionSelected}>
      <div className="flex flex-col items-stretch gap-4 pt-4 max-w-[800px]">
        <div>
          <div className="flex flex-row justify-between">
            <h6 className="font-semibold text-sm">
              {accountName
                ? t('moreZoomIntegrationsWithAccount', { accountName })
                : t('moreZoomIntegrations')}
            </h6>
            <OnOffSwitch
              data-testid="more-zoom-integrations-toggle"
              checked={enabled}
              onCheckedChange={onEnabledChanged}
            />
          </div>
          <p className="text-sm text-subtle">
            {t('yourNotetakerCanStillJoinMeetings')}
          </p>
        </div>
        {enabled && (
          <>
            <Label
              caption={t('liveNotesDescription')}
              control={
                <RadioGroupItem value={ValidZoomServiceType.LIVE_NOTES} />
              }
            >
              {t('liveNotes')}
            </Label>
            <Label
              caption={t('cloudSyncDescription')}
              control={
                <RadioGroupItem value={ValidZoomServiceType.CLOUD_SYNC} />
              }
            >
              {t('cloudSync')}
            </Label>
          </>
        )}
      </div>
    </RadioGroup>
  );
}
/**
 * Should show the settings button above the disconnect button.
 * Migrated from https://github.com/otter-ai/otter-web/blob/f1e94a61d6413de26239cf2646ae3096c6b55510/packages/client/src/app/containers/dashboard/account-settings/connected-apps/connected-apps.component.ts#L2007
 */
export function useCanShowConnectionSettings(): boolean {
  const { accounts } = useSyncedAccounts({ service: BUILT_IN_APPS.ZOOM });
  const { serviceSettings } = useZoomSettings();
  const { needsToUpgradeForAdvancedZoomFeatures } =
    useMoreZoomIntegrationsStatus();

  if (accounts.length === 0) {
    return false;
  }
  const hasErrorRequiringReconnect = showZoomReconnect(accounts);
  if (hasErrorRequiringReconnect || needsToUpgradeForAdvancedZoomFeatures) {
    return false;
  }

  const hasCloudSync = serviceSettings.some(
    (serviceSetting) =>
      serviceSetting.has[ValidZoomServiceType.CLOUD_SYNC] === true,
  );
  const enabledCloudSync = serviceSettings.some(
    (serviceSetting) =>
      serviceSetting.enabled[ValidZoomServiceType.CLOUD_SYNC] === true,
  );

  if (enabledCloudSync && hasCloudSync) {
    return true;
  }

  const enabledLiveNotes = serviceSettings.some(
    (serviceSetting) =>
      serviceSetting.enabled[ValidZoomServiceType.LIVE_NOTES] === true,
  );
  const settingIssuesLiveNotes = serviceSettings.some((serviceSetting) =>
    Boolean(serviceSetting.issues[ValidZoomServiceType.LIVE_NOTES]?.length),
  );

  if (enabledLiveNotes && (hasCloudSync || !settingIssuesLiveNotes)) {
    return true;
  }
  return false;
}
function pickEnabledOption(
  serviceSettings: ZoomAccountServiceSettings,
): ValidZoomServiceType | null {
  const enabledServices = Object.entries(ValidZoomServiceType)
    .filter(([_, serviceType]) => {
      return serviceSettings.enabled[serviceType] === true;
    })
    .map<ValidZoomServiceType>(([_, serviceType]) => serviceType);

  if (enabledServices.length > 1) {
    // TODO(SER-14251): Prevent this from happening on the backend
    // TODO(sleahy): Some users do have both enabled, and this error blocks them
    // throw new MultipleServicesEnabledError(enabledServices);
  }

  if (enabledServices.length === 0) {
    return null;
  }

  return enabledServices[0];
}

// class MultipleServicesEnabledError extends Error {
//   name = 'MultipleServicesEnabledError';
//   constructor(public enabledServices: ValidZoomServiceType[]) {
//     super(
//       `Multiple services are enabled for the same Zoom account: ${enabledServices.join(', ')}`,
//     );
//   }
// }
